import { DateField } from './DateField';

export { Select } from './Select';
export { CellTags, ControlPopover, MultiSelectCell } from './TableControls';

import {
  Archive,
  BetweenHorizontalStart,
  CalendarPlus,
  CalendarX,
  FilePlus2,
  ListFilterPlus,
  ListOrdered,
  ListPlus,
  Plus,
  RotateCcw,
  Search,
  Shredder,
  Target,
  Trash2,
  Undo2,
  UserPlus,
  X,
} from 'lucide-react';
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type ComponentType,
  type FormEventHandler,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SVGProps,
  type TextareaHTMLAttributes,
  useRef,
} from 'react';

import { RouteLink } from '../../lib/routes';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ControlSize = 'default' | 'compact';

const classes = (...values: (string | undefined | false)[]) => values.filter(Boolean).join(' ');

export function Button({
  variant = 'secondary',
  size = 'default',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ControlSize }) {
  return (
    <button
      {...props}
      className={classes(
        'ui-button',
        `ui-button--${variant}`,
        size === 'compact' && 'ui-control--compact',
        className,
      )}
    />
  );
}

export function ButtonLink({
  variant = 'secondary',
  size = 'default',
  className,
  reloadDocument = false,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  variant?: ButtonVariant;
  size?: ControlSize;
  reloadDocument?: boolean;
}) {
  if (reloadDocument) {
    return (
      <a
        {...props}
        className={classes(
          'ui-button',
          `ui-button--${variant}`,
          size === 'compact' && 'ui-control--compact',
          className,
        )}
      />
    );
  }

  return (
    <RouteLink
      {...props}
      className={classes(
        'ui-button',
        `ui-button--${variant}`,
        size === 'compact' && 'ui-control--compact',
        className,
      )}
    />
  );
}

export function IconButton({
  label,
  variant = 'ghost',
  size = 'default',
  className,
  title,
  type = 'button',
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & {
  label: string;
  variant?: ButtonVariant;
  size?: ControlSize;
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      {...props}
      type={type}
      aria-label={label}
      title={title ?? label}
      className={classes(
        'ui-icon-button',
        `ui-icon-button--${variant}`,
        size === 'compact' && 'ui-control--compact',
        className,
      )}
    />
  );
}

/**
 * Lucide has no key-plus, so compose one the way its own creation icons are
 * built: the base glyph plus a 6-unit plus in the free bottom-right corner.
 * `KeyRound`'s key runs bottom-left to top-right, so nothing needs clipping.
 */
function KeyPlus(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
      <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
      <path d="M16 18h6" />
      <path d="M19 15v6" />
    </svg>
  );
}

const actionIcons = {
  add: Plus,
  note: FilePlus2,
  task: ListPlus,
  person: UserPlus,
  event: CalendarPlus,
  goal: Target,
  vault: KeyPlus,
  restore: RotateCcw,
  trash: Trash2,
  delete: Shredder,
  drop: Archive,
  reverse: Undo2,
  cancelEvent: CalendarX,
  filter: ListFilterPlus,
  sort: ListOrdered,
  column: Plus,
  row: BetweenHorizontalStart,
};

export type IconAction = keyof typeof actionIcons;

const creationActions: IconAction[] = [
  'add',
  'note',
  'task',
  'person',
  'event',
  'goal',
  'vault',
  'filter',
  'sort',
  'column',
  'row',
];

/** Stable icon meanings and color hierarchy for the same action on every screen. */
export function ActionIconButton({
  action,
  variant,
  ...props
}: Omit<ComponentProps<typeof IconButton>, 'children'> & { action: IconAction }) {
  const Icon = actionIcons[action];

  return (
    <IconButton
      {...props}
      variant={
        variant ??
        (action === 'trash' || action === 'delete'
          ? 'danger'
          : creationActions.includes(action)
            ? 'primary'
            : 'secondary')
      }
    >
      <Icon aria-hidden="true" />
    </IconButton>
  );
}

export function Input({
  className,
  variant = 'default',
  type = 'text',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  ref?: Ref<HTMLInputElement>;
  variant?: 'default' | 'inline' | 'cell' | 'capture';
}) {
  if (type === 'date' || type === 'datetime-local') {
    return (
      <DateField
        {...props}
        type={type}
        variant={variant === 'capture' ? 'default' : variant}
        className={className}
      />
    );
  }

  const choice = type === 'checkbox' || type === 'radio';

  return (
    <input
      {...props}
      type={type}
      className={classes(
        choice ? 'ui-choice' : type === 'file' ? 'ui-file-input' : 'ui-input',
        variant === 'inline' && 'ui-input--inline',
        variant === 'cell' && 'ui-input--cell',
        variant === 'capture' && 'ui-input--capture',
        className,
      )}
    />
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={classes('ui-textarea', className)} />;
}

export function SearchField({
  label,
  value,
  onChange,
  placeholder = label,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    // biome-ignore lint/a11y/useSemanticElements: <search> support on the target iOS versions is unverified; the div keeps an explicit role
    <div className="ui-search" role="search" aria-label={label}>
      <Search aria-hidden="true" size={18} />
      <input
        ref={input}
        type="search"
        aria-label={label}
        placeholder={placeholder}
        autoComplete="off"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape' && value) {
            e.preventDefault();
            e.stopPropagation();
            onChange('');
          }
        }}
      />
      {value && (
        <IconButton
          label={`Clear ${label.toLocaleLowerCase()}`}
          onClick={() => {
            onChange('');
            input.current?.focus();
          }}
        >
          <X aria-hidden="true" size={18} />
        </IconButton>
      )}
    </div>
  );
}

export function InlineCreate({
  label,
  value,
  onChange,
  onSubmit,
  action,
  icon = 'task',
  placeholder,
  required = true,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  action: string;
  icon?: IconAction;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <form className="ui-inline-create" onSubmit={onSubmit}>
      <Input
        aria-label={label}
        required={required}
        maxLength={500}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <ActionIconButton type="submit" action={icon} label={action} disabled={disabled} />
    </form>
  );
}

export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  children,
  compact = false,
}: {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  compact?: boolean;
}) {
  return (
    <header className={classes('ui-page-header', compact && 'ui-page-header--compact')}>
      <div className="ui-page-heading">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
        {children}
      </div>
      {actions && <div className="ui-page-actions">{actions}</div>}
    </header>
  );
}

export type Tone = 'neutral' | 'priority' | 'info' | 'focus' | 'success' | 'attention' | 'danger';

/**
 * A symbol on a small tile of its role's colour, leading a row that opens a
 * page — the phone's `IconTile`. The same size and corners everywhere.
 */
export function IconTile({
  icon: Icon,
  tone,
  size = 'default',
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  tone: Tone;
  size?: 'default' | 'small';
}) {
  return (
    <span
      className={classes('ui-icon-tile', `tone-${tone}`, size === 'small' && 'ui-icon-tile--small')}
    >
      <Icon aria-hidden="true" />
    </span>
  );
}

/**
 * A compact action shaped like a pill: a window of time that can be taken
 * away, or a way to begin a sentence — the phone's `ChipActionStyle`. It may
 * carry a symbol beside its words, which a labelled Button may not.
 */
export function Chip({
  tone,
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone }) {
  return (
    <button
      {...props}
      type={type}
      className={classes('ui-chip', tone && `tone-${tone}`, className)}
    />
  );
}

/**
 * A quiet way out — "Not now", "Not today", "Later": muted words, no fill,
 * and a target that still reaches the control height.
 */
export function QuietButton({
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} type={type} className={classes('ui-quiet-button', className)} />;
}

/** Directional navigation keeps its explicit destination label. */

/** Directional navigation keeps its explicit destination label. */

export { Disclosure } from './Disclosure';
export { MobileDetails, useMobileLayout } from './MobileDetails';
export { Modal } from './Modal';
export { NoteIcon, noteIconGlyph } from './NoteIcon';
export { Palette, type PaletteItem } from './Palette';
export { Sheet, SheetTitle } from './Sheet';
